import { useState } from 'react';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { ParticipantsImport } from './ParticipantsImport';

const Section = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(8)};
  }
`;

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ControlBtn = styled.button`
  margin-left: ${({ theme }) => theme.spacing(2)};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 640px;
`;

const AssignmentRow = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;

  & + & {
    margin-top: ${({ theme }) => theme.spacing(2)};
  }
`;

const AssignmentText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AssignmentMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

const ActionBtn = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  font-size: 13px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  border: 1px solid
    ${({ theme, $variant }) => ($variant === 'ghost' ? theme.colors.border : theme.colors.primary)};
  background: ${({ theme, $variant }) => ($variant === 'ghost' ? 'transparent' : theme.colors.primary)};
  color: ${({ theme, $variant }) => ($variant === 'ghost' ? theme.colors.text.secondary : '#fff')};

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }
`;

const AddRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const StatusSelect = styled.select`
  font-size: 12px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
`;

const ParticipantInput = styled.input`
  font-size: 13px;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export interface MeetingParticipantItem {
  id: string;
  name: string;
  vk_chat_id?: string | null;
  user_id?: string | null;
}

export interface CandidateUser {
  id: string;
  full_name: string;
  department?: string | null;
}

export interface DirectoryEmployee {
  id: string;
  full_name: string;
  department?: string | null;
  vk_chat_id?: string | null;
  user_id?: string | null;
}

export interface MeetingParticipantsSectionProps {
  meetingId: string;
  participants: MeetingParticipantItem[];
  protocolPresent: boolean;
  canWrite: boolean;
  candidates: CandidateUser[];
  directoryOptions: DirectoryEmployee[];
  onRematchParticipants: () => void;
  rematchingParts: boolean;
  partsInfo: string | null;
  onAddParticipant: (data: { name: string; vk_chat_id?: string; user_id?: string }) => void;
  onAddFromDirectory: (empId: string) => void;
  adding: boolean;
  onDeleteParticipant: (id: string) => void;
}

export function MeetingParticipantsSection({
  meetingId,
  participants,
  protocolPresent,
  canWrite,
  candidates,
  directoryOptions,
  onRematchParticipants,
  rematchingParts,
  partsInfo,
  onAddParticipant,
  onAddFromDirectory,
  adding,
  onDeleteParticipant,
}: MeetingParticipantsSectionProps) {
  const [pName, setPName] = useState('');
  const [pVk, setPVk] = useState('');
  const [pUserId, setPUserId] = useState('');
  const [pEmpId, setPEmpId] = useState('');

  function handleAdd() {
    if (!pName.trim()) return;
    onAddParticipant({
      name: pName.trim(),
      vk_chat_id: pVk.trim() || undefined,
      user_id: pUserId || undefined,
    });
    setPName('');
    setPVk('');
    setPUserId('');
  }

  function handleDirectoryAdd() {
    if (!pEmpId) return;
    onAddFromDirectory(pEmpId);
    setPEmpId('');
  }

  return (
    <Section>
      <SectionLabel>
        Участники (рассылка протокола)
        {canWrite && protocolPresent && (
          <ControlBtn
            type="button"
            onClick={onRematchParticipants}
            disabled={rematchingParts}
          >
            {rematchingParts ? 'Собираю…' : 'Пересобрать из протокола'}
          </ControlBtn>
        )}
      </SectionLabel>
      {partsInfo && <EmptyHint>{partsInfo}</EmptyHint>}
      {participants.length > 0 ? (
        <div>
          {participants.map((p) => (
            <AssignmentRow key={p.id}>
              <div>
                <AssignmentText>{p.name}</AssignmentText>
                <AssignmentMeta>
                  {p.vk_chat_id ? `VK: ${p.vk_chat_id}` : 'без рассылки'}
                  {p.user_id ? ' · учётка привязана' : ''}
                </AssignmentMeta>
              </div>
              {canWrite && (
                <ActionBtn
                  $variant="ghost"
                  onClick={() => onDeleteParticipant(p.id)}
                >
                  Удалить
                </ActionBtn>
              )}
            </AssignmentRow>
          ))}
        </div>
      ) : (
        <EmptyHint>
          Участников нет.{canWrite ? ' Добавьте — им разошлётся утверждённый протокол.' : ''}
        </EmptyHint>
      )}
      {canWrite && directoryOptions.length > 0 && (
        <AddRow>
          <StatusSelect
            value={pEmpId}
            onChange={(e) => setPEmpId(e.target.value)}
            aria-label="Добавить участника из справочника"
          >
            <option value="">— выбрать из справочника сотрудников —</option>
            {directoryOptions.map((e) => (
              <option key={e.id} value={e.id}>
                {e.full_name}
                {e.department ? ` · ${e.department}` : ''}
                {e.vk_chat_id ? '' : ' · без VK'}
              </option>
            ))}
          </StatusSelect>
          <ActionBtn onClick={handleDirectoryAdd} disabled={adding || !pEmpId}>
            Добавить
          </ActionBtn>
        </AddRow>
      )}
      {canWrite && (
        <AddRow>
          <ParticipantInput
            value={pName}
            onChange={(e) => setPName(e.target.value)}
            placeholder="Имя участника"
            aria-label="Имя участника"
          />
          <ParticipantInput
            value={pVk}
            onChange={(e) => setPVk(e.target.value)}
            placeholder="VK chat id (опц.)"
            aria-label="VK chat id"
          />
          <StatusSelect
            value={pUserId}
            onChange={(e) => setPUserId(e.target.value)}
            aria-label="Привязать учётку (опц.)"
          >
            <option value="">— без учётки —</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.full_name}
                {c.department ? ` · ${c.department}` : ''}
              </option>
            ))}
          </StatusSelect>
          <ActionBtn
            onClick={handleAdd}
            disabled={adding || !pName.trim()}
          >
            Добавить
          </ActionBtn>
        </AddRow>
      )}
      {canWrite && <ParticipantsImport meetingId={meetingId} />}
    </Section>
  );
}
